import { useEffect, useRef, useState } from "preact/hooks";

const KEY = "cloudmail.mailPaneWidth";
const clamp = (value: number, width: number) => Math.max(280, Math.min(value, width - 330));

/** A pointer and keyboard adjustable reading pane, scoped to this browser. */
export const PaneDivider = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const [width, setWidth] = useState<number | null>(() => {
    try {
      const saved = Number(localStorage.getItem(KEY));
      return Number.isFinite(saved) && saved >= 280 ? saved : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent || width === null) return;
    const apply = () => {
      if (parent.clientWidth < 610) return;
      parent.style.setProperty("--mail-pane-width", `${clamp(width, parent.clientWidth)}px`);
    };
    const observer = new ResizeObserver(apply);
    observer.observe(parent);
    apply();
    try {
      localStorage.setItem(KEY, String(width));
    } catch {
      /* A browser may disable local storage. */
    }
    return () => {
      observer.disconnect();
      parent.style.removeProperty("--mail-pane-width");
    };
  }, [width]);
  const change = (value: number) => {
    const parent = ref.current?.parentElement;
    if (parent && parent.clientWidth >= 610) setWidth(clamp(value, parent.clientWidth));
  };
  return (
    <button
      ref={ref}
      type="button"
      class="pane-divider"
      data-part="pane-divider"
      aria-label="Resize message list. Use left and right arrow keys."
      title="Drag to resize; double-click to reset"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
        const left = event.currentTarget.parentElement?.getBoundingClientRect().left;
        if (left !== undefined) change(event.clientX - left);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onDblClick={() => {
        setWidth(null);
        try {
          localStorage.removeItem(KEY);
        } catch {
          /* Storage can be unavailable. */
        }
      }}
      onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        const current = ref.current?.previousElementSibling?.getBoundingClientRect().width ?? 400;
        change(current + (event.key === "ArrowRight" ? 24 : -24));
      }}
    >
      <span />
    </button>
  );
};
